import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BfiDataGrid, Column, MasterDetail } from '../../common/SharedComponents';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoanStatusType, LoanType } from '../common/LoanConstants';
import { getLoanTypeShortName, getLoanGridSettingId, onRenderPortfolioGrid } from '../common/LoanFunctions';
import {
    onActionCellRender,
    onBorrowerCellRender,
    onCityCellRender,
    onStateCellRender,
    OnOpenLoanDetail,
    OnOpenLoanSummary,
    onRowPrepared,
} from '../common/LoanFunctions';
import { LoanCommitOrApprovalColumn } from '../../common/SharedComponents';
import { LoanApprovedOrNoteDateColumn } from '../common/components/LoanApprovedOrNoteDateColumn';
import { onStructureTypeCellRender } from '../common/functions/onStructureTypeCellRender';
import { onLoanTermCellRender } from '../common/functions/onLoanTermCellRender';
import { onRepaidDateCellRender } from '../common/functions/onRepaidDateCellRender';
import { onExtensionCellRender } from '../common/functions/onExtensionCellRender';

const SubNotesTable = ({ gridStore, statusId = LoanStatusType.Outstanding }) => {
    const searchState = useSelector((state) => state.searchState);
    const loanApi = new LoanApi.LoanApi(applicationInsights);
    const location = useLocation();

    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => {
        criteria.statusId = statusId;
        return loanApi.getGrid_SubNotes(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.loan);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    if (!gridStore) {
        return null;
    }

    return (
        <BfiDataGrid
            gridType={getLoanGridSettingId(LoanType.SubNoteConstruction, statusId)}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellDblClick={(e) =>
                statusId === LoanStatusType.Approved ||
                statusId === LoanStatusType.Outstanding ||
                statusId === LoanStatusType.Repaid
                    ? OnOpenLoanDetail(e, location)
                    : OnOpenLoanSummary(e, location)
            }
        >
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                cellRender={onBorrowerCellRender}
                minWidth={120}
            />
            <Column
                dataField='borrowerCity'
                caption='Borr. City'
                cellRender={onCityCellRender}
                width={90}
            />
            <Column
                dataField='borrowerState'
                caption='Borr. State'
                cellRender={onStateCellRender}
                width={70}
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={120}
            />
            <Column
                dataField='loanNumber'
                caption='Loan #'
                dataType='string'
                width={80}
            />
            <Column
                dataField={'loanTypeId'}
                caption='Loan Type'
                width={90}
                cellRender={onLoanTypeCellRender}
                alignment='left'
            />
            <Column
                dataField='structureTypeId'
                caption='Structure Type'
                dataType='string'
                cellRender={onStructureTypeCellRender}
                width={120}
            />
            {LoanCommitOrApprovalColumn({ statusId })}
            {LoanApprovedOrNoteDateColumn({ statusId })}
            <Column
                dataField='initialTermMonths'
                caption='Term'
                dataType='string'
                width={60}
                alignment={'left'}
                cellRender={onLoanTermCellRender}
            />
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='repaidDate'
                    caption='Repaid Date'
                    dataType='string'
                    width={110}
                    alignment={'left'}
                    cellRender={onRepaidDateCellRender}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='totalRepaidAmount'
                    caption='Repaid Amt'
                    dataType='number'
                    format='currency'
                    width={110}
                    alignment={'left'}
                />
            )}
            {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='extensionNumber'
                    caption='Ext.'
                    dataType='string'
                    width={40}
                    alignment='left'
                    cellRender={onExtensionCellRender}
                />
            )}
            {/* TODO: Future work related to Defaults */}
            {/* {statusId === LoanStatusType.Repaid && (
                <Column
                    dataField='defaults'
                    caption='Defaults'
                    dataType='string'
                    width={110}
                />
            )} */}
            <Column
                dataField='loanToValue'
                caption='LTV'
                dataType='number'
                format={{ type: 'percent', precision: 1 }}
                width={50}
                alignment={'left'}
            />
            <Column
                dataField='pointOfContact'
                caption='POC'
                dataType='string'
                width={120}
            />
            <Column
                dataField='ownerName'
                caption='User'
                dataType='string'
                width={120}
            />
            <Column
                dataField='broker'
                dataType='string'
                width={120}
            />
            <Column
                dataField='lostType'
                caption={'Reason'}
                dataType='string'
                width={80}
                visible={statusId === LoanStatusType.Lost}
            />
            <Column
                dataField='lostReason'
                caption='Details'
                dataType='string'
                width={200}
                visible={statusId === LoanStatusType.Lost}
            />
            <Column
                alignment='center'
                cellRender={onActionCellRender}
                width={50}
                allowReordering={false}
                allowSorting={false}
                allowResizing={false}
                type='actions'
                fixed={true}
                fixedPosition={'right'}
            />
            <MasterDetail
                enabled={true}
                render={(e) => onRenderPortfolioGrid(e, statusId)}
            />
        </BfiDataGrid>
    );
};

export default SubNotesTable;
